/**
 * Which environment is this page being served from, and does the baked-in
 * Firebase config agree with it?
 *
 * WHY THIS IS SEPARATE FROM firebase.ts
 * The check has to run before `initializeApp`, and importing firebase.ts to
 * test it would initialize Firebase as a side effect. Keeping the decision pure
 * makes it directly testable against real hostnames — which is how the gap
 * below was found.
 *
 * WHAT WAS WRONG BEFORE
 * The guard classified hosts with substring guesses:
 *
 *   isDevHost  = host === 'localhost' || host.startsWith('admin-dev.') || host.includes('-dev.')
 *   isProdHost = host === 'admin.tradegladiator.com'
 *
 * Both are blind on the URLs actually in use. The Firebase sites are
 * `tradegladiator-dev-admin` and `tradegladiator-admin` (see .firebaserc), so
 * the day-to-day hosts are:
 *
 *   tradegladiator-dev-admin.web.app  — contains "-dev-", NOT "-dev.", so it
 *                                       matched no dev pattern
 *   tradegladiator-admin.web.app      — is not the custom prod domain, so it
 *                                       matched no prod pattern
 *
 * A guard that exists to stop a prod-configured bundle being served to dev was
 * therefore inert on the dev site, and the reverse guard inert on the prod
 * site. Both only covered the custom domains.
 *
 * The fix matches the SITE NAME explicitly, including the forms Firebase
 * Hosting actually serves: `<site>.web.app`, `<site>.firebaseapp.com`, and
 * preview channels `<site>--<channel>-<hash>.web.app`.
 */

export const PROD_PROJECT_ID = 'trading-journal-dae7f';
export const DEV_PROJECT_ID = 'trading-journal-dev-94c32';

/** Firebase Hosting site IDs, from .firebaserc `targets.<project>.hosting.admin-portal`. */
export const PROD_SITE = 'tradegladiator-admin';
export const DEV_SITE = 'tradegladiator-dev-admin';

export const PROD_DOMAIN = 'admin.tradegladiator.com';
export const DEV_DOMAIN = 'admin-dev.tradegladiator.com';

export type HostEnvironment = 'dev' | 'prod' | 'unknown';

/**
 * Every hostname Firebase Hosting serves a given site on.
 *
 * The `--` form is a preview channel (`site--pr-42-a1b2c3.web.app`). It is
 * matched by prefix because the channel name and hash are not predictable.
 */
function servesSite(hostname: string, site: string): boolean {
  return (
    hostname === `${site}.web.app` ||
    hostname === `${site}.firebaseapp.com` ||
    hostname.startsWith(`${site}--`)
  );
}

/**
 * Classify a hostname. Dev is tested first: `tradegladiator-dev-admin` does not
 * collide with `tradegladiator-admin` under any of the forms above, but the
 * ordering keeps that true if a future site name makes one a prefix of the other.
 *
 * `unknown` is deliberate and not a failure — an unrecognised host (a new custom
 * domain, a local IP, an internal preview proxy) simply gets no assertion made
 * about it, rather than being guessed into the wrong environment.
 */
export function classifyHost(hostname: string): HostEnvironment {
  if (!hostname) return 'unknown';

  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') return 'dev';
  if (hostname === DEV_DOMAIN || hostname.startsWith('admin-dev.')) return 'dev';
  if (servesSite(hostname, DEV_SITE)) return 'dev';
  // Retained from the original guard: catches ad-hoc dev subdomains such as
  // `admin-dev.example.com` that the explicit lists above do not name.
  if (hostname.includes('-dev.')) return 'dev';

  if (hostname === PROD_DOMAIN) return 'prod';
  if (servesSite(hostname, PROD_SITE)) return 'prod';

  return 'unknown';
}

/**
 * Returns an explanatory message when the host and the baked-in project
 * disagree, or null when they are consistent (or the host is unrecognised).
 *
 * Vite bakes `import.meta.env.VITE_*` at BUILD time, so the failure this
 * catches is a build-and-deploy mismatch — `npm run build` defaults to
 * `--mode production`, and deploying that output to dev would otherwise point
 * the dev admin portal at production data with no visible sign.
 */
export function detectEnvironmentMismatch(
  hostname: string,
  projectId: string | undefined
): string | null {
  if (!projectId) return null;
  const env = classifyHost(hostname);

  if (env === 'dev' && projectId === PROD_PROJECT_ID) {
    return (
      `Build/environment mismatch: dev host (${hostname}) is configured with PROD ` +
      `Firebase project (${projectId}). Refusing to initialize Firebase to prevent ` +
      'writes against production. Rebuild with `npm run build:dev` and redeploy.'
    );
  }
  if (env === 'prod' && projectId === DEV_PROJECT_ID) {
    return (
      `Build/environment mismatch: prod host (${hostname}) is configured with DEV ` +
      `Firebase project (${projectId}). Refusing to initialize. Rebuild with ` +
      '`npm run build:prod` and redeploy.'
    );
  }
  return null;
}
